import React, { ReactNode, RefObject, UIEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';

import style from '@core/Page/Page.module.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';
import { ConfirmPopupParams, ExistingReferralPopupParams, SimplePopupParams } from '@store/App/App';

import { ScrollBar } from '@components/ScrollBar';
import { Preloader } from '@components/Preloader';

import { SavePreloader } from '@components/SavePreloader';
import { PageRouter } from '@core/Router/PageRouter';
import { ConfirmPopup, InformPopup } from '@components/Popup';
import { PhoneWidget } from '@components/PhoneWidget';
import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { BlockedEhrBanner } from '@components/BlockedEhrBanner';
import { ExistingReferralPopup } from '@core/Page/ExistingReferralPopup';
import { SelectOrganizationPopupParams } from '@core/Page/SelectOrganizationPopup/SelectOrganizationPopup';

import { LeftMenu } from '@core/Page/LeftMenu';
import { Header } from '@core/Page/Header';
import { RightMenu } from '@core/Page/RightMenu';
import { SelectOrganizationPopup } from '@core/Page/SelectOrganizationPopup';
import { ForbiddenPopup } from '@core/Page/ForbiddenPopup';
import { DraftEntriesBanner } from '@core/Page/DraftEntriesBanner';

import { PAGE_ELEMENT_ID } from './constants';

import '@core/Page/reset.scss';
import { Overlay } from '@store/App/Overlay';
import { ClickOutsideMask } from '@components/ClickOutsideMask';
import { AppPortal } from '@components/AppPortal/AppPortal';
import { IndexDBNames, IndexDBStoreNames, useIndexedDB } from '@components/Dictaphone/hooks/useIndexDB';
import { HeaderComponents } from '@core/Page/Header/types';
import { HeaderCompontDic } from '@core/Page/Header/Header';

export interface PageProps {
    children?: ReactNode;
}

export interface PageComponentProps {}

interface Props extends PageProps, PageComponentProps {
    isPhoneWidgetHide: boolean;
    rootFixed?: boolean;
    appOverlay?: Overlay;
    phoneWidgetOpen: boolean;
    withContentNavigation: boolean;
    greyBackground: boolean;
    isOnLoad: boolean;
    isOnUpdate: boolean;
    showLoader: boolean;
    needAuthorize: boolean;
    forbiddenResources: boolean;
    hideScrollBar: boolean;
    withoutHeader: boolean;
    withoutLeftMenu: boolean;
    withPaddingTop: boolean;
    withPaddingBottom: boolean;
    withoutCustomScroll: boolean;
    contentRef: RefObject<HTMLDivElement | null>;
    additionalLeftMenuContent?: HeaderComponents;
    showBlockBanner: boolean;
    blockBannerUrl?: string;
    showingPopupParams: Nullable<SimplePopupParams>;
    confirmPopupParams: Nullable<ConfirmPopupParams>;
    existingReferralPopup?: ExistingReferralPopupParams;
    selectOrganizationPopupParams?: SelectOrganizationPopupParams;
    isAuthorized: boolean;
    scrollBarRef?: React.RefObject<HTMLDivElement | null>;
    onClosePopupClick(): void;
    onAuthorizeClick(): void;
    onScroll(e: UIEvent<HTMLElement>): void;
    onClickOutsidePhoneMask(): void;
}

export function Page({
    phoneWidgetOpen,
    rootFixed,
    appOverlay,
    isOnLoad,
    isOnUpdate,
    showLoader,
    isPhoneWidgetHide,
    showBlockBanner,
    blockBannerUrl,
    greyBackground,
    hideScrollBar,
    forbiddenResources,
    withoutLeftMenu,
    withoutHeader,
    withPaddingTop,
    withPaddingBottom,
    withoutCustomScroll,
    additionalLeftMenuContent,
    contentRef,
    withContentNavigation,
    showingPopupParams,
    confirmPopupParams,
    existingReferralPopup,
    selectOrganizationPopupParams,
    isAuthorized,
    scrollBarRef,
    onScroll,
    onAuthorizeClick,
    onClosePopupClick,
    onClickOutsidePhoneMask,
}: Props) {
    useIndexedDB({ dbName: IndexDBNames.AudioDB, storeName: IndexDBStoreNames.Recordings });

    const observerRef = useRef<ResizeObserver | null>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    const headerRef = useCallback((el: HTMLDivElement | null) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (!el) {
            setHeaderHeight(0);
            return;
        }

        setHeaderHeight(el.getBoundingClientRect().height);

        const observer = new ResizeObserver(([entry]) => {
            setHeaderHeight(entry.contentRect.height);
        });

        observer.observe(el);
        observerRef.current = observer;
    }, []);

    useLayoutEffect(() => {
        document.body.style.setProperty('--header-height', `${headerHeight}px`);
    }, [headerHeight]);

    useLayoutEffect(() => {
        const el = document.createElement('div');
        el.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;overflow-x:hidden';
        document.body.appendChild(el);

        const update = () => {
            document.body.style.setProperty('--scrollbar-width', `${el.offsetWidth - el.clientWidth}px`);
        };

        // It's known that this is needed when switching from overlay to classic scrollbars in Chrome on MacOS
        const forceUpdate = () => {
            el.style.overflow = 'hidden';
            // eslint-disable-next-line sonarjs/void-use
            void el.offsetWidth;
            el.style.overflow = 'scroll';
        };

        update();

        const observer = new ResizeObserver(update);
        observer.observe(el);
        window.addEventListener('focus', forceUpdate);

        return () => {
            observer.disconnect();
            window.removeEventListener('focus', forceUpdate);
            document.body.removeChild(el);
        };
    }, []);

    function getContent() {
        return (
            <div
                className={classnames([
                    style.content,
                    withPaddingTop && style.contentPaddingTop,
                    withPaddingBottom && style.contentPaddingBottom,
                ])}
                id={withoutCustomScroll ? PAGE_ELEMENT_ID : undefined}
                ref={contentRef}
            >
                {showingPopupParams !== null && (
                    <InformPopup
                        onClose={onClosePopupClick}
                        {...showingPopupParams}
                    />
                )}
                {confirmPopupParams !== null && (
                    <ConfirmPopup
                        onCancel={onClosePopupClick}
                        {...confirmPopupParams}
                    />
                )}
                {existingReferralPopup && <ExistingReferralPopup {...existingReferralPopup} />}
                <PageRouter />
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>cms</title>
            </Helmet>
            {phoneWidgetOpen && (
                <ClickOutsideAlerter onClickOutside={onClickOutsidePhoneMask}>
                    <div className={style.phoneWidget}>
                        <div className={isPhoneWidgetHide ? style.hide : ''}>
                            <PhoneWidget />
                        </div>
                    </div>
                </ClickOutsideAlerter>
            )}
            <div
                className={classnames([
                    style.root,
                    rootFixed && style.root_fixed,
                    greyBackground && style.root_greyBackground,
                    withoutHeader && style.root_withoutHeader,
                    withContentNavigation && style.root_withContentNavigation,
                    withoutCustomScroll && style.root_withoutCustomScroll,
                ])}
            >
                {!withoutLeftMenu && (
                    <div className={style.leftMenu}>
                        <LeftMenu
                            additionalContent={
                                !!additionalLeftMenuContent ? HeaderCompontDic[additionalLeftMenuContent] : undefined
                            }
                        />
                    </div>
                )}
                {(isOnLoad || isOnUpdate || showLoader) && (
                    <div className={style.preloader}>
                        <Preloader />
                    </div>
                )}
                {appOverlay && (
                    <AppPortal>
                        <ClickOutsideMask
                            transparentBackground={appOverlay.transparentBackground}
                            zIndex={appOverlay.zIndex}
                            onClickOutside={appOverlay.onClickOutside}
                        />
                    </AppPortal>
                )}
                <RightMenu />
                {forbiddenResources && isAuthorized && <ForbiddenPopup />}
                {selectOrganizationPopupParams && <SelectOrganizationPopup {...selectOrganizationPopupParams} />}
                <div className={style.center}>
                    <div className={style.topLine}>
                        {!withoutHeader && (
                            <div
                                ref={headerRef}
                                className={classnames([style.headerWrapper])}
                            >
                                <Header />
                            </div>
                        )}
                        {isAuthorized && <DraftEntriesBanner />}
                        {showBlockBanner && (
                            <BlockedEhrBanner
                                onEhrPage
                                url={blockBannerUrl}
                            />
                        )}
                    </div>
                    {withoutCustomScroll ? (
                        getContent()
                    ) : (
                        <ScrollBar
                            ref={scrollBarRef}
                            hide={hideScrollBar}
                            scrollComponentId={PAGE_ELEMENT_ID}
                            onScroll={onScroll}
                        >
                            {getContent()}
                        </ScrollBar>
                    )}
                </div>
                <SavePreloader />
            </div>
        </>
    );
}
